/**
 * 사전/사후 제출 1건(survey_submissions)에 대응해 survey_submission_results 행을 채울 점수·상세.
 */

function parseTimeToMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

function toInt(s, d = 0) {
  if (s === null || s === undefined || s === '') return d;
  const n = parseInt(String(s), 10);
  return Number.isFinite(n) ? n : d;
}

function sleepLatencyQ2Score(minutes) {
  if (minutes < 0 || !Number.isFinite(minutes)) return 0;
  if (minutes <= 15) return 0;
  if (minutes <= 30) return 1;
  if (minutes <= 60) return 2;
  return 3;
}

function mapSumTo0123(sum) {
  if (sum === 0) return 0;
  if (sum >= 1 && sum <= 2) return 1;
  if (sum >= 3 && sum <= 4) return 2;
  if (sum >= 5 && sum <= 6) return 3;
  if (sum > 6) return 3;
  return 0;
}

function mapSum5to0123(rawSum) {
  if (rawSum === 0) return 0;
  if (rawSum >= 1 && rawSum <= 9) return 1;
  if (rawSum >= 10 && rawSum <= 18) return 2;
  if (rawSum >= 19 && rawSum <= 27) return 3;
  if (rawSum > 27) return 3;
  return 0;
}

function comp3FromTotalHours(h) {
  if (!Number.isFinite(h) || h < 0) return 0;
  if (h >= 7) return 0;
  if (h >= 6) return 1;
  if (h >= 5) return 2;
  return 3;
}

function comp4FromEfficiency(eff) {
  if (!Number.isFinite(eff) || eff < 0) return 3;
  if (eff >= 85) return 0;
  if (eff >= 75) return 1;
  if (eff >= 65) return 2;
  return 3;
}

const PSQI_DIST_KEYS = ['q5b', 'q5c', 'q5d', 'q5e', 'q5f', 'q5g', 'q5h', 'q5i', 'q5j2'];

/**
 * @param {Record<string, string>} a — answers[psqi] 평면 객체
 */
export function scorePsqi(a) {
  const q6 = toInt(a.q6, 0);
  const comp1 = Math.min(3, Math.max(0, q6));

  const h2 = toInt(a.q2h, 0);
  const m2 = toInt(a.q2m, 0);
  const minLatency = h2 * 60 + m2;
  const s2 = sleepLatencyQ2Score(minLatency);
  const s5a = toInt(a.q5a, 0);
  const c2raw = s2 + s5a;
  const comp2 = mapSumTo0123(c2raw);

  const q4h = toInt(a.q4h, 0);
  const q4m = toInt(a.q4m, 0);
  const totalHours = q4h + q4m / 60;
  const comp3 = comp3FromTotalHours(totalHours);

  const bedMin = parseTimeToMinutes(a.q1);
  const wakeMin = parseTimeToMinutes(a.q3);
  let tibMin = null;
  if (bedMin != null && wakeMin != null) {
    tibMin = wakeMin - bedMin;
    if (tibMin <= 0) tibMin += 24 * 60;
  }
  const tstMin = q4h * 60 + q4m;
  let efficiency = null;
  if (tibMin != null && tibMin > 0) {
    const tstClamped = Math.min(tstMin, tibMin);
    efficiency = (tstClamped / tibMin) * 100;
  }
  const comp4 = efficiency != null ? comp4FromEfficiency(efficiency) : 3;

  const raw5 = PSQI_DIST_KEYS.reduce((s, k) => s + toInt(a[k], 0), 0);
  const comp5 = mapSum5to0123(raw5);

  const comp6 = Math.min(3, Math.max(0, toInt(a.q7, 0)));

  const s8 = toInt(a.q8, 0);
  const s9 = toInt(a.q9, 0);
  const c7raw = s8 + s9;
  const comp7 = mapSumTo0123(c7raw);

  const global = comp1 + comp2 + comp3 + comp4 + comp5 + comp6 + comp7;

  return {
    global,
    components: { c1: comp1, c2: comp2, c3: comp3, c4: comp4, c5: comp5, c6: comp6, c7: comp7 },
    _meta: {
      minLatency,
      c2raw,
      totalHours,
      tibMin,
      tstMin,
      efficiency,
      raw5,
      c7raw,
    },
  };
}

/**
 * @param {Record<string, string>} a — answers[isi]
 */
export function scoreIsi(a) {
  const keys = ['1a', '1b', '1c', '2', '3', '4', '5'];
  let total = 0;
  for (const k of keys) {
    total += toInt(a[k], 0);
  }
  return { total, items: Object.fromEntries(keys.map((k) => [k, toInt(a[k], 0)])) };
}

const BERLIN_SCORES = {
  q1: { y: 1, n: 0, u: 0 },
  q2: { a: 0, b: 0, c: 1, d: 1 },
  q3: { d: 1, w34: 1, w12: 0, m12: 0, r: 0 },
  q4: { y: 1, n: 0, u: 0 },
  q5: { d: 1, w34: 1, w12: 0, m12: 0, r: 0 },
  q6: { d: 1, w34: 1, w12: 0, m12: 0, r: 0 },
  q7: { d: 1, w34: 1, w12: 0, m12: 0, r: 0 },
  q8: { y: 1, n: 0 },
  q9: { d: 1, w34: 1, w12: 0, m12: 0, r: 0 },
  q10: { y: 1, n: 0 },
};

function berlinItemScore(key, v) {
  if (v == null || v === '') return 0;
  return BERLIN_SCORES[key]?.[String(v)] ?? 0;
}

/**
 * Q1~5(코골이·수면무호흡) ≥2, Q6~9(낮졸음 등) ≥2, BMI>30 또는 고혈압: 각 1점.
 * @param {Record<string, string|number>} a
 */
export function scoreBerlin(a) {
  const s1 = berlinItemScore('q1', a.q1);
  const s2 = berlinItemScore('q2', a.q2);
  const s3 = berlinItemScore('q3', a.q3);
  const s4 = berlinItemScore('q4', a.q4);
  const s5 = berlinItemScore('q5', a.q5);
  const part1 = s1 + s2 + s3 + s4 + s5;

  const s6 = berlinItemScore('q6', a.q6);
  const s7 = berlinItemScore('q7', a.q7);
  const s8y = a.q8 === 'y' ? 1 : 0;
  const s9 = a.q8 === 'y' ? berlinItemScore('q9', a.q9) : 0;
  const part2 = s6 + s7 + s8y + s9;

  const hCm = toInt(a.h, 0) || 0;
  const wKg = toInt(a.w, 0) || 0;
  let bmi = null;
  if (hCm > 0 && wKg > 0) {
    bmi = wKg / (hCm / 100) ** 2;
  }
  const r3Bmi = bmi != null && Number.isFinite(bmi) && bmi > 30;
  const r3Htn = a.q10 === 'y';
  const r3 = r3Bmi || r3Htn;

  const r1 = part1 >= 2;
  const r2 = part2 >= 2;

  const berlinPositiveRegionCount = (r1 ? 1 : 0) + (r2 ? 1 : 0) + (r3 ? 1 : 0);

  return {
    berlinPositiveRegionCount,
    part1,
    part2,
    bmi,
    r1,
    r2,
    r3,
  };
}

/**
 * @param {Record<string, string>} a
 */
export function scoreOabss(a) {
  const keys = ['oabss_1', 'oabss_2', 'oabss_3', 'oabss_4'];
  let oabssSum = 0;
  const items = {};
  for (const k of keys) {
    const v = toInt(a[k], 0);
    items[k] = v;
    oabssSum += v;
  }
  return { oabssSum, items };
}

/**
 * @param {string} code — survey_instruments.code
 * @param {Record<string, string>} answers
 * @returns {{ psqi_global?: number|null, isi_total?: number|null, berlin_positive_region_count?: number|null, oabss_sum?: number|null, details: object }|null}
 */
export function buildRowForInstrument(code, answers) {
  if (!answers || typeof answers !== 'object') {
    return { psqi_global: null, isi_total: null, berlin_positive_region_count: null, oabss_sum: null, details: {} };
  }
  if (code === 'psqi') {
    const p = scorePsqi(answers);
    return {
      psqi_global: p.global,
      isi_total: null,
      berlin_positive_region_count: null,
      oabss_sum: null,
      details: { psqi: p },
    };
  }
  if (code === 'isi') {
    const i = scoreIsi(answers);
    return {
      psqi_global: null,
      isi_total: i.total,
      berlin_positive_region_count: null,
      oabss_sum: null,
      details: { isi: i },
    };
  }
  if (code === 'berlin') {
    const b = scoreBerlin(answers);
    return {
      psqi_global: null,
      isi_total: null,
      berlin_positive_region_count: b.berlinPositiveRegionCount,
      oabss_sum: null,
      details: { berlin: b },
    };
  }
  if (code === 'oabss') {
    const o = scoreOabss(answers);
    return {
      psqi_global: null,
      isi_total: null,
      berlin_positive_region_count: null,
      oabss_sum: o.oabssSum,
      details: { oabss: o },
    };
  }
  return null;
}
